import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Undo2, X } from 'lucide-react';
import { Popover } from './Popover';

/** The phone's selected-note tools stay within reach while the score scrolls. */
export function ScoreNoteEditor({
  pitchLabel,
  rest,
  duration,
  durationLabel,
  durations,
  fixedDuration,
  dotted,
  tied,
  canUndo,
  canRedo,
  canPaste,
  previousId,
  nextId,
  onNavigate,
  onPitch,
  onDuration,
  onDot,
  onTie,
  onAccidental,
  onRespell,
  onUndo,
  onRedo,
  onCopy,
  onCut,
  onPaste,
  onDelete,
  onClose,
  staffControls,
  expressionControls,
  noteheadControls,
}: {
  pitchLabel: string;
  rest: boolean;
  duration: number;
  durationLabel: string;
  durations: { label: string; beats: number }[];
  fixedDuration: boolean;
  dotted: boolean;
  tied: boolean;
  canUndo: boolean;
  canRedo: boolean;
  canPaste: boolean;
  previousId: string | null;
  nextId: string | null;
  onNavigate: (id: string) => void;
  onPitch: (semitones: number) => void;
  onDuration: (duration: number) => void;
  onDot: () => void;
  onTie: () => void;
  onAccidental: (accidental: 'natural' | 'sharp' | 'flat') => void;
  onRespell: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onClose: () => void;
  staffControls?: ReactNode;
  expressionControls?: ReactNode;
  noteheadControls?: ReactNode;
}) {
  const knownDuration = durations.some((option) => Math.abs(option.beats - duration) < 1e-9);
  return (
    <div
      className="score-mobile-note-editor no-print"
      role="group"
      aria-label="Selected note"
      data-testid="score-mobile-note-editor"
    >
      <div className="score-mobile-pitch-row">
        <button
          className="btn btn-sm"
          type="button"
          aria-label="Previous note or rest"
          disabled={!previousId}
          onClick={() => previousId && onNavigate(previousId)}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <button
          className="btn btn-sm"
          type="button"
          aria-label="Lower pitch"
          disabled={rest}
          onClick={() => onPitch(-1)}
        >
          <ArrowDown size={18} aria-hidden="true" />
        </button>
        <span className="score-mobile-pitch" aria-live="polite">
          {pitchLabel}
        </span>
        <button
          className="btn btn-sm"
          type="button"
          aria-label="Raise pitch"
          disabled={rest}
          onClick={() => onPitch(1)}
        >
          <ArrowUp size={18} aria-hidden="true" />
        </button>
        <button
          className="btn btn-sm"
          type="button"
          aria-label="Next note or rest"
          disabled={!nextId}
          onClick={() => nextId && onNavigate(nextId)}
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
        <button
          className="close-circle"
          type="button"
          aria-label="Finish editing note"
          onClick={onClose}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      {noteheadControls}
      <div className="score-mobile-duration-row">
        <select
          aria-label="Note duration"
          value={duration}
          disabled={fixedDuration}
          title={fixedDuration ? 'Tuplet and grace note durations are fixed' : 'Note duration'}
          onChange={(event) => onDuration(Number(event.target.value))}
        >
          {!knownDuration && <option value={duration}>{durationLabel}</option>}
          {durations.map((option) => (
            <option key={option.beats} value={option.beats}>
              {option.label}
            </option>
          ))}
        </select>
        <button className="btn btn-sm" type="button" disabled={!canUndo} onClick={onUndo}>
          <Undo2 size={16} aria-hidden="true" /> Undo
        </button>
        <Popover label="More" title="More note actions" align="right" testid="score-note-more">
          {(close) => {
            const perform = (action: () => void) => () => {
              action();
              close();
            };
            return (
              <div className="score-mobile-more-actions">
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Toggle dotted duration"
                  aria-pressed={dotted}
                  disabled={fixedDuration}
                  onClick={perform(onDot)}
                >
                  Dot
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Toggle tie"
                  aria-pressed={tied}
                  disabled={rest}
                  onClick={perform(onTie)}
                >
                  Tie
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={!canRedo}
                  onClick={perform(onRedo)}
                >
                  Redo
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Natural"
                  disabled={rest}
                  onClick={perform(() => onAccidental('natural'))}
                >
                  ♮ Natural
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Sharp"
                  disabled={rest}
                  onClick={perform(() => onAccidental('sharp'))}
                >
                  ♯ Sharp
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Flat"
                  disabled={rest}
                  onClick={perform(() => onAccidental('flat'))}
                >
                  ♭ Flat
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Lower octave"
                  disabled={rest}
                  onClick={perform(() => onPitch(-12))}
                >
                  Octave ↓
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  aria-label="Raise octave"
                  disabled={rest}
                  onClick={perform(() => onPitch(12))}
                >
                  Octave ↑
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={rest}
                  onClick={perform(onRespell)}
                >
                  Respell
                </button>
                <button className="btn btn-sm" type="button" onClick={perform(onCopy)}>
                  Copy
                </button>
                <button className="btn btn-sm" type="button" onClick={perform(onCut)}>
                  Cut
                </button>
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={!canPaste}
                  onClick={perform(onPaste)}
                >
                  Paste
                </button>
                <button className="btn btn-sm" type="button" onClick={perform(onDelete)}>
                  Delete
                </button>
                {expressionControls && (
                  <div style={{ gridColumn: '1 / -1' }}>{expressionControls}</div>
                )}
                {staffControls && (
                  <div className="score-mobile-staff-controls">{staffControls}</div>
                )}
              </div>
            );
          }}
        </Popover>
      </div>
    </div>
  );
}
